import React from "react";
import { Button } from "./ui/button";
import {
  Check,
  Crown,
  ExternalLink,
  Lock,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { subscription, partnerTools, STRIPE_CHECKOUT_URL } from "../mock";

const CreditMasteryPremium = () => {
  return (
    <section
      id="credit-mastery"
      className="relative bg-black text-white py-20 md:py-28 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #14b8a6 0%, transparent 45%), radial-gradient(circle at 85% 70%, #0d9488 0%, transparent 45%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-teal-400/15 border border-teal-400/40 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <Crown className="w-3.5 h-3.5" />
            <span>Premium · Credit Mastery</span>
          </div>
          <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
            The credit module,{" "}
            <em className="text-teal-300 font-serif font-normal">
              fully unlocked.
            </em>
          </h2>
          <p className="mt-5 text-white/65 text-lg max-w-2xl">
            {subscription.description}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Subscription card */}
          <div className="lg:col-span-5">
            <div className="relative h-full rounded-3xl border border-teal-400/30 bg-gradient-to-br from-[#04181a] to-black p-8 md:p-10 overflow-hidden">
              <div
                className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full blur-3xl opacity-30"
                style={{
                  background:
                    "radial-gradient(circle, #14b8a6 0%, transparent 70%)",
                }}
              />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 bg-teal-400 text-black text-[11px] font-semibold px-3 py-1 rounded-full tracking-wide uppercase">
                  <Crown className="w-3 h-3" />
                  {subscription.highlight}
                </span>
                <div className="mt-5">
                  <div className="text-2xl md:text-3xl font-semibold tracking-tight">
                    {subscription.name}
                  </div>
                  <div className="mt-1.5 text-teal-300 text-sm font-medium">
                    {subscription.tagline}
                  </div>
                </div>

                <div className="mt-7 flex items-baseline gap-1.5">
                  <span className="text-5xl md:text-6xl font-semibold tracking-tight text-white">
                    ${subscription.price}
                  </span>
                  <span className="text-white/55 text-base">
                    /{subscription.cadence}
                  </span>
                </div>
                <div className="mt-1 text-xs text-white/50">
                  Cancel anytime · No long-term contract
                </div>

                <a
                  href={STRIPE_CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-8"
                >
                  <Button className="w-full bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12">
                    {subscription.cta}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <div className="mt-2 text-center text-[11px] text-white/45">
                  Secure checkout powered by Stripe
                </div>

                <ul className="mt-8 space-y-3">
                  {subscription.benefits.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-white/85 text-sm"
                    >
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-teal-400/20 border border-teal-400/40 text-teal-300 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Partner tools */}
          <div className="lg:col-span-7 space-y-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-400/30 text-teal-300 flex items-center justify-center shrink-0">
                  <Lock className="w-4.5 h-4.5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">
                    Members unlock two partner tools
                  </div>
                  <div className="mt-1 text-sm text-white/60 leading-relaxed">
                    We&apos;ve hand-picked credit tools that pair with the
                    curriculum so you can act on what you learn — monitor your
                    credit and repair it, the DIY way.
                  </div>
                </div>
              </div>
            </div>

            {partnerTools.map((p) => {
              const Icon = p.id === "identityiq" ? ShieldCheck : Wrench;
              const isTeal = p.accent === "teal";
              return (
                <article
                  key={p.id}
                  className={`group rounded-2xl p-7 border transition-all hover:-translate-y-0.5 ${
                    isTeal
                      ? "bg-[#04181a] border-teal-400/30"
                      : "bg-white/[0.03] border-white/10 hover:border-teal-400/30"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                          isTeal
                            ? "bg-teal-400 text-black"
                            : "bg-white/10 text-teal-300"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-teal-300 font-medium">
                          {p.category}
                        </div>
                        <h3 className="mt-1 text-xl md:text-2xl font-semibold text-white tracking-tight">
                          {p.name}
                        </h3>
                      </div>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-teal-300 border border-teal-400/30 rounded-full px-2.5 py-1 whitespace-nowrap">
                      Partner tool
                    </span>
                  </div>
                  <p className="mt-4 text-white/65 leading-relaxed">
                    {p.summary}
                  </p>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {p.bullets.map((b) => (
                      <div
                        key={b}
                        className="flex items-center gap-2.5 text-sm text-white/80"
                      >
                        <span className="w-4 h-4 rounded-full bg-teal-400/15 border border-teal-400/30 text-teal-300 flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                        </span>
                        {b}
                      </div>
                    ))}
                  </div>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="mt-6 inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold text-sm border-b border-teal-400/30 hover:border-teal-300 pb-0.5 transition-colors"
                  >
                    Visit {p.name}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </article>
              );
            })}

            {/* Affiliate disclosure */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-xs text-white/50 leading-relaxed">
              <strong className="text-white/70 font-semibold">
                Affiliate disclosure:
              </strong>{" "}
              The links to IdentityIQ and CreditScoreIQ are partner referral
              links. Good Fruit may earn a small commission if you sign up
              through them — at no extra cost to you. We only partner
              with tools we believe genuinely help our members flourish.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreditMasteryPremium;
